/**
 * 사진 EXIF 파싱 · WebP 최적화 (DOC-27 §4).
 *
 * <h2>순서가 중요하다</h2>
 * <b>EXIF 를 먼저 뽑고 그 다음에 압축한다.</b> canvas 를 거치면 EXIF 가 통째로 사라지므로,
 * 순서를 바꾸면 좌표와 촬영시각을 영영 잃는다.
 *
 * <h2>왜 라이브러리를 안 쓰나</h2>
 * 필요한 태그가 셋뿐이다(촬영시각·위도·경도). exifr 급 라이브러리는 그 셋을 위해 번들에
 * 수십 KB 를 더한다. 아래 파서는 JPEG APP1 의 IFD 만 읽고 나머지는 무시한다.
 */

export type PhotoExif = {
  shotAt: Date | null;
  latitude: number | null;
  longitude: number | null;
  /** 파싱 과정에서 읽은 원본 태그. 왜 못 뽑았는지를 나중에 본다(§8 `exif_raw`). */
  raw: Record<string, string | number>;
};

const TAG_DATETIME_ORIGINAL = 0x9003;
const TAG_EXIF_IFD = 0x8769;
const TAG_GPS_IFD = 0x8825;
const GPS_LAT_REF = 0x0001;
const GPS_LAT = 0x0002;
const GPS_LON_REF = 0x0003;
const GPS_LON = 0x0004;

/**
 * JPEG 에서 촬영시각·GPS 를 읽는다. 태그가 없으면 null 을 담아 돌려준다 —
 * <b>실패가 아니다.</b> 메신저를 거친 사진·위치 권한을 끈 기기는 원래 좌표가 없고,
 * 그런 사진을 거부하면 현장은 실험실을 두 번째로 열지 않는다(§4.3).
 */
export function parseExif(buffer: ArrayBuffer): PhotoExif {
  const empty: PhotoExif = { shotAt: null, latitude: null, longitude: null, raw: {} };
  const view = new DataView(buffer);
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return empty; // SOI 아님 = JPEG 아님

  // APP1(Exif) 세그먼트를 찾는다.
  let offset = 2;
  let tiffStart = -1;
  while (offset + 4 <= view.byteLength) {
    if (view.getUint8(offset) !== 0xff) break;
    const marker = view.getUint8(offset + 1);
    const size = view.getUint16(offset + 2);
    if (marker === 0xe1 && offset + 10 <= view.byteLength && view.getUint32(offset + 4) === 0x45786966) {
      tiffStart = offset + 10; // 'Exif\0\0' 다음
      break;
    }
    if (marker === 0xda) break; // 이미지 데이터 시작 — 더 볼 것 없다
    offset += 2 + size;
  }
  if (tiffStart < 0 || tiffStart + 8 > view.byteLength) return empty;

  const little = view.getUint16(tiffStart) === 0x4949;
  const raw: Record<string, string | number> = {};

  const readEntries = (ifdOffset: number): Map<number, { type: number; count: number; valueOffset: number }> => {
    const entries = new Map<number, { type: number; count: number; valueOffset: number }>();
    const base = tiffStart + ifdOffset;
    if (base + 2 > view.byteLength) return entries;
    const count = view.getUint16(base, little);
    for (let i = 0; i < count; i++) {
      const entry = base + 2 + i * 12;
      if (entry + 12 > view.byteLength) break;
      entries.set(view.getUint16(entry, little), {
        type: view.getUint16(entry + 2, little),
        count: view.getUint32(entry + 4, little),
        valueOffset: entry + 8,
      });
    }
    return entries;
  };

  const readAscii = (entry: { count: number; valueOffset: number }): string => {
    const length = entry.count;
    const start = length > 4 ? tiffStart + view.getUint32(entry.valueOffset, little) : entry.valueOffset;
    let text = '';
    for (let i = 0; i < length && start + i < view.byteLength; i++) {
      const code = view.getUint8(start + i);
      if (code === 0) break;
      text += String.fromCharCode(code);
    }
    return text;
  };

  /** GPS 좌표는 도·분·초 세 유리수로 들어온다. */
  const readDms = (entry: { count: number; valueOffset: number }): number | null => {
    if (entry.count < 3) return null;
    const start = tiffStart + view.getUint32(entry.valueOffset, little);
    if (start + 24 > view.byteLength) return null;
    const part = (i: number) => {
      const numerator = view.getUint32(start + i * 8, little);
      const denominator = view.getUint32(start + i * 8 + 4, little);
      return denominator === 0 ? 0 : numerator / denominator;
    };
    return part(0) + part(1) / 60 + part(2) / 3600;
  };

  try {
    const ifd0 = readEntries(view.getUint32(tiffStart + 4, little));

    let shotAt: Date | null = null;
    const exifPointer = ifd0.get(TAG_EXIF_IFD);
    if (exifPointer) {
      const exifIfd = readEntries(view.getUint32(exifPointer.valueOffset, little));
      const dateEntry = exifIfd.get(TAG_DATETIME_ORIGINAL);
      if (dateEntry) {
        const text = readAscii(dateEntry);
        raw.DateTimeOriginal = text;
        shotAt = parseExifDate(text);
      }
    }

    let latitude: number | null = null;
    let longitude: number | null = null;
    const gpsPointer = ifd0.get(TAG_GPS_IFD);
    if (gpsPointer) {
      const gpsIfd = readEntries(view.getUint32(gpsPointer.valueOffset, little));
      const latEntry = gpsIfd.get(GPS_LAT);
      const lonEntry = gpsIfd.get(GPS_LON);
      const latRef = gpsIfd.get(GPS_LAT_REF);
      const lonRef = gpsIfd.get(GPS_LON_REF);
      if (latEntry && lonEntry) {
        const lat = readDms(latEntry);
        const lon = readDms(lonEntry);
        if (lat !== null && lon !== null) {
          const south = latRef ? readAscii(latRef).startsWith('S') : false;
          const west = lonRef ? readAscii(lonRef).startsWith('W') : false;
          latitude = south ? -lat : lat;
          longitude = west ? -lon : lon;
          raw.GPSLatitude = latitude;
          raw.GPSLongitude = longitude;
        }
      }
    }

    return { shotAt, latitude, longitude, raw };
  } catch {
    // 태그가 규격을 벗어난 사진이 실제로 있다. 사진을 버리지 않고 좌표만 포기한다.
    return { ...empty, raw };
  }
}

/** EXIF 날짜는 `YYYY:MM:DD HH:MM:SS`. Date 생성자가 그대로는 못 읽는다. */
export function parseExifDate(text: string): Date | null {
  const match = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/.exec(text.trim());
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);
  // 촬영시각이 설정되지 않은 기기는 `0000:00:00 00:00:00` 을 적는다. Date 생성자는 그것을
  // 1899년으로 받아 주므로 여기서 걸러야 한다 — 아니면 1899년 작업일보가 생긴다.
  if (year < 1900 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const date = new Date(year, month - 1, day, Number(h), Number(mi), Number(s));
  return Number.isNaN(date.getTime()) ? null : date;
}

export type CompressProfile = {
  /** 장변 상한 (px). */
  maxEdge: number;
  /** 0~1. */
  quality: number;
};

export const DEFAULT_PROFILE: CompressProfile = { maxEdge: 1920, quality: 0.8 };

/**
 * 서류(PDF·엑셀)에 실릴 사진의 프로파일. 형식이 JPEG 라 품질을 조금 올려 잡는다 —
 * 같은 눈금이 아니다(WebP q80 ≒ JPEG q82~85).
 */
export const DOCUMENT_PROFILE: CompressProfile = { maxEdge: 1920, quality: 0.82 };

/** 프로파일 식별자. 행마다 기록해 두면 나중에 화질을 올려도 과거를 구분할 수 있다(§4.3). */
export function profileId(profile: CompressProfile): string {
  return `webp-q${Math.round(profile.quality * 100)}-${profile.maxEdge}`;
}

/** 형식이 다르면 식별자도 달라야 한다 — 나중에 "이 사진은 무엇으로 저장됐나" 를 이 값 하나로 판단한다. */
export function jpegProfileId(profile: CompressProfile): string {
  return `jpeg-q${Math.round(profile.quality * 100)}-${profile.maxEdge}`;
}

/** 장변을 상한에 맞춘 크기. 원본이 이미 작으면 확대하지 않는다. */
export function fitSize(width: number, height: number, maxEdge: number): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxEdge) return { width, height };
  const scale = maxEdge / longest;
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export type CompressResult = {
  blob: Blob;
  width: number;
  height: number;
  originalBytes: number;
  bytes: number;
  profile: string;
};

/**
 * 원본 → WebP. <b>클라이언트에서 한다</b> — 현장 LTE 로 15MB 를 올리는 것이 병목이고,
 * 서버 압축은 그 병목을 그대로 둔 채 저장 비용만 줄인다(§4.2).
 */
export async function compressToWebp(
  file: File,
  profile: CompressProfile = DEFAULT_PROFILE,
): Promise<CompressResult> {
  return compress(file, 'image/webp', profile, profileId(profile));
}

/**
 * 원본 → JPEG. <b>서류에 실릴 사진은 이쪽이다.</b>
 *
 * <h2>왜 WebP 가 아닌가</h2>
 * 용량만 보면 WebP 가 낫다. 그런데 <b>PDF·엑셀 어느 쪽도 WebP 를 그리지 못한다</b> —
 * PDFBox 에 WebP 디코더가 없고, POI 의 그림 형식 목록에도 없다. 그대로 두면 사진을 붙인
 * 일보에서 사진 자리만 비는데, 그 빈칸은 "사진이 없었다" 로 읽힌다.
 *
 * <p>1920px q82 기준 WebP 대비 20~40% 크지만, 그래도 원본(5~15MB)의 한 자릿수 퍼센트다.
 * <b>산출물에 실리지 않는 작은 파일보다 실리는 큰 파일이 낫다.</b>
 */
export async function compressToJpeg(
  file: File,
  profile: CompressProfile = DOCUMENT_PROFILE,
): Promise<CompressResult> {
  return compress(file, 'image/jpeg', profile, jpegProfileId(profile));
}

async function compress(
  file: File,
  mime: 'image/webp' | 'image/jpeg',
  profile: CompressProfile,
  id: string,
): Promise<CompressResult> {
  const bitmap = await createImageBitmap(file);
  const { width, height } = fitSize(bitmap.width, bitmap.height, profile.maxEdge);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('캔버스를 만들 수 없습니다.');
  // JPEG 는 투명을 모른다. 흰 바탕을 먼저 깔지 않으면 투명 픽셀이 검게 나온다.
  if (mime === 'image/jpeg') {
    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);
  }
  context.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, mime, profile.quality));
  if (!blob) throw new Error('이미지 변환에 실패했습니다.');

  return {
    blob,
    width,
    height,
    originalBytes: file.size,
    bytes: blob.size,
    profile: id,
  };
}
